/**
 * Features por país del tenant — espejo MÍNIMO del módulo canónico del admin
 * (goautos-admin/src/lib/country.ts), igual que lib/currency es espejo del
 * suyo. Solo se replica lo que el sitio público necesita.
 *
 * `fuel_types` es un catálogo GLOBAL en Supabase (gasolina, diesel, híbrido,
 * eléctrico, flex, gnv, etanol...). El wizard del admin filtra por país, pero
 * el sitio público traía la tabla entera: los sitios chilenos mostraban chips
 * "flex"/"gnv" (y mostrarían "etanol") sin ningún vehículo que los use.
 *
 * Mantener en sincronía con hiddenFuelTypes del admin.
 */

const DEFAULT_COUNTRY = 'CL';

/** Nombres de fuel_types que cada país NO usa (comparación case-insensitive). */
const HIDDEN_FUEL_TYPES: Record<string, string[]> = {
  CL: ['flex', 'gnv', 'etanol'],
  BR: [],
  // Expansión LATAM: GNV/GNC es común en Colombia y Argentina; flex/etanol no.
  CO: ['flex', 'etanol'],
  AR: ['flex', 'etanol'],
  // EE.UU.: flex fuel (E85) existe; GNV y etanol puro no se venden.
  US: ['gnv', 'etanol'],
};

/** Filtra el catálogo global de combustibles según el país del tenant (null/desconocido → CL). */
export const visibleFuelTypes = <T extends { name: string | null }>(
  fuelTypes: T[],
  country?: string | null
): T[] => {
  const code = String(country || DEFAULT_COUNTRY).toUpperCase();
  const hidden = HIDDEN_FUEL_TYPES[code] ?? HIDDEN_FUEL_TYPES[DEFAULT_COUNTRY];
  if (hidden.length === 0) return fuelTypes;
  return fuelTypes.filter(
    (ft) => !hidden.includes((ft.name || '').trim().toLowerCase())
  );
};

/**
 * Idioma OBLIGATORIO del sitio público según el país del tenant.
 *
 * Un sitio de Brasil sale SIEMPRE en portugués y uno de EE.UU. SIEMPRE en
 * inglés: no lo cambia el navegador del visitante, ni un idioma guardado de una
 * visita anterior, ni el selector. Para el resto (CL / CO / AR / null /
 * desconocido) devuelve null = el sitio decide el idioma exactamente como hasta
 * hoy (incluido el camino de `default_language` de los tenants chilenos en 'en').
 *
 * `pt` NO se registra como idioma elegible (AVAILABLE_LANGUAGES): así nunca
 * aparece en el selector de un sitio chileno.
 */
export type ForcedSiteLanguage = 'pt' | 'en';

const FORCED_LANGUAGE: Record<string, ForcedSiteLanguage | null> = {
  CL: null,
  BR: 'pt',
  // Hispanohablantes: el sitio decide el idioma como siempre (es).
  CO: null,
  AR: null,
  // EE.UU.: inglés obligatorio, igual que Brasil con el portugués.
  US: 'en',
};

export const codeOf = (country?: string | null): string =>
  String(country || DEFAULT_COUNTRY).toUpperCase();

export const forcedSiteLanguage = (
  country?: string | null
): ForcedSiteLanguage | null => FORCED_LANGUAGE[codeOf(country)] ?? null;

/** `<html lang>`: 'pt-BR' para Brasil, 'en-US' para EE.UU.; el resto, el 'es' de siempre. */
export const htmlLangOf = (country?: string | null): string => {
  const forzado = forcedSiteLanguage(country);
  return forzado === 'pt' ? 'pt-BR' : forzado === 'en' ? 'en-US' : 'es';
};

/** `og:locale`: 'pt_BR' para Brasil, 'en_US' para EE.UU.; el resto, el 'es_CL' de siempre. */
export const ogLocaleOf = (country?: string | null): string => {
  const forzado = forcedSiteLanguage(country);
  return forzado === 'pt' ? 'pt_BR' : forzado === 'en' ? 'en_US' : 'es_CL';
};

/**
 * Texto fijo según el idioma OBLIGATORIO del país: `pt` (BR), `en` (US) o `es`
 * para todos los demás (CL / CO / AR / null / desconocido: lo de siempre,
 * aunque el tenant tenga default_language 'en'). Para los textos que no pasan
 * por el i18n (metadata, correos, SEO del servidor, módulos sin React).
 */
export const porIdiomaDelPais = <T,>(
  country: string | null | undefined,
  textos: { es: T; pt: T; en: T }
): T => textos[forcedSiteLanguage(country) ?? 'es'];

/**
 * Nombre genérico del rubro, para cuando el tenant no tiene nombre cargado.
 * 'Automotora' es el de siempre; Brasil 'Concessionária'; EE.UU. 'Car Dealership'.
 */
export const rubroDelPais = (country?: string | null): string =>
  porIdiomaDelPais(country, { es: 'Automotora', pt: 'Concessionária', en: 'Car Dealership' });

/**
 * ¿El país lee la hora en 12 horas ("2:30 PM")? Solo EE.UU.; el resto, 'HH:mm'
 * de 24 horas como siempre.
 */
export const reloj12hDe = (country?: string | null): boolean => codeOf(country) === 'US';

/**
 * Hora 'HH:mm' (24 h) tal como la muestra el país. EE.UU. → "2:30 PM", "12:00 PM",
 * "12:05 AM". Cualquier otro país, o un texto que no sea 'H:mm'/'HH:mm', vuelve
 * TAL CUAL (byte-idéntico a lo de siempre). Solo para MOSTRAR: lo que se compara
 * o se guarda sigue en 'HH:mm'.
 */
export const horaDelPais = (hhmm: string, country?: string | null): string => {
  if (!reloj12hDe(country)) return hhmm;
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? '').trim());
  if (!m) return hhmm;
  const h = Number(m[1]);
  if (h > 23) return hhmm;
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
};

/**
 * ¿El tenant es de Brasil? Para gatear lo que es propio de cada país (RUT vs
 * CPF, teléfono +56 vs +55, comunas, patente chilena, IVA chileno…). Siempre
 * por PAÍS: nunca por moneda ni por default_language.
 */
export const isBrasil = (country?: string | null): boolean => codeOf(country) === 'BR';

/**
 * ¿El tenant es de EE.UU.? Mismo criterio que `isBrasil`: siempre por PAÍS.
 * Para los textos se prefiere `forcedSiteLanguage(country) === 'en'` /
 * `porIdiomaDelPais`.
 */
export const isUsa = (country?: string | null): boolean => codeOf(country) === 'US';

/**
 * Código telefónico del país sin '+' (espejo de `CountryFeatures.dialCode` del
 * admin). Lo usan el botón de WhatsApp y el teléfono de la ficha para normalizar
 * un número cargado sin código. Desconocido/null → Chile, como todo lo demás.
 */
const DIAL_CODE: Record<string, string> = {
  CL: '56',
  BR: '55',
  CO: '57',
  AR: '54',
  US: '1',
};

export const dialCodeOf = (country?: string | null): string =>
  DIAL_CODE[codeOf(country)] ?? DIAL_CODE[DEFAULT_COUNTRY];

/**
 * Regla de dígitos de un país: dígitos crudos (sin '+', espacios ni guiones) →
 * dígitos internacionales sin '+' (los que van en `wa.me/<n>`), o `null` si el
 * número no se reconoce para ese país. Las reglas no adivinan: un número que no
 * calza vuelve `null` y la pantalla decide (esconder el botón, no mostrar el
 * teléfono) en vez de armar un link roto.
 */
export type PhoneDigitsRule = (digits: string) => string | null;

/** `0055…` → `55…`: prefijo internacional "00" (BR, CO y AR; Chile lo trata caso por caso). */
const sin00 = (d: string): string => (d.startsWith('00') ? d.slice(2) : d);

/** Chile: móvil `569XXXXXXXX` (11 dígitos). La regla de siempre de `contact-utils`, tal cual. */
const chile: PhoneDigitsRule = (d) => {
  if (d.length === 11 && d.startsWith('569')) return d;
  if (d.length === 9 && d.startsWith('9')) return `56${d}`;
  if (d.length === 8) return `569${d}`;
  if (d.length === 10 && d.startsWith('56')) return `569${d.slice(2)}`;
  if (d.length === 12 && d.startsWith('0569')) return d.slice(1);
  if (d.length === 13 && d.startsWith('00569')) return d.slice(2);
  return null;
};

/**
 * Brasil: `55` + DDD + número (12 o 13 dígitos). La regla de siempre de
 * `contact-utils`, tal cual.
 * - Con código de país (+55 / 0055): se respeta tal cual.
 * - Local con DDD (10 u 11 dígitos, fijo o celular): se le antepone `55`. Va
 *   por LARGO y no por prefijo: el DDD 55 existe (RS), así que
 *   "55 99999-9999" es un número local y no uno con código de país.
 * - Sin DDD (8 o 9 dígitos) o cualquier otro largo: `null`. No hay forma de
 *   saber el área, y jamás se convierte en un móvil chileno `569…`.
 */
const brasil: PhoneDigitsRule = (digitos) => {
  const d = sin00(digitos);
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) return d;
  if (d.length === 10 || d.length === 11) return `55${d}`;
  return null;
};

/** Colombia: móviles de 10 dígitos (empiezan en 3); `wa_id` = 57 + 10 dígitos. */
const colombia: PhoneDigitsRule = (digitos) => {
  const d = sin00(digitos);
  if (d.length === 12 && d.startsWith('57')) return d;
  if (d.length === 10) return `57${d}`;
  return null;
};

/**
 * Argentina — `área (2-4 dígitos) + 15 + número` → `área + número` (10 dígitos),
 * SOLO si el "15" cae en una única posición posible (después de 2, 3 o 4
 * dígitos de área). Si podría estar en más de una (p.ej. `11 15 15xx-xxxx`) no
 * se adivina: `null`.
 */
const sin15 = (local12: string): string | null => {
  const posiciones = [2, 3, 4].filter((i) => local12.slice(i, i + 2) === '15');
  if (posiciones.length !== 1) return null;
  const i = posiciones[0];
  return local12.slice(0, i) + local12.slice(i + 2);
};

/**
 * Argentina: `wa_id` = 549 + área + número (13 dígitos), CON el 9 que los
 * móviles llevan entre el 54 y el área cuando se marcan desde el exterior
 * (wa.me y Meta lo exigen; sin él el chat no abre).
 *   - 549 + 10 dígitos → tal cual.
 *   - 54 + 10 dígitos (sin el 9: `+54 11 1234-5678`) → se inserta el 9.
 *   - 10 dígitos (área + número) → 549 + eso.
 *   - 0 + 10 dígitos (marcado nacional) → se saca el 0.
 *   - [0 +] área + 15 + número (celular marcado dentro del país) → se saca el
 *     15 solo si su posición es inequívoca (ver `sin15`).
 *   - cualquier otra cosa → `null`.
 */
const argentina: PhoneDigitsRule = (digitos) => {
  const d = sin00(digitos);
  if (d.length === 13 && d.startsWith('549')) return d;
  if (d.length === 12 && d.startsWith('54') && !d.startsWith('549')) return `549${d.slice(2)}`;
  if (d.length === 10) return `549${d}`;
  if (d.length === 11 && d.startsWith('0')) return `549${d.slice(1)}`;
  const local = d.length === 13 && d.startsWith('0') ? d.slice(1) : d;
  if (local.length === 12 && !local.startsWith('54')) {
    const limpio = sin15(local);
    if (limpio) return `549${limpio}`;
  }
  return null;
};

/**
 * Estados Unidos (plan NANP, +1): `wa_id` = 1 + 10 dígitos (área de 3 que no
 * empieza en 0 ni 1, + 7).
 *   - 1 + 10 dígitos → tal cual.
 *   - 10 dígitos de NANP → 1 + eso.
 *   - 011 + número (prefijo internacional de US) → sin el 011.
 *   - cualquier otra cosa → `null` (jamás un móvil chileno `569…`).
 */
const estadosUnidos: PhoneDigitsRule = (d) => {
  if (d.length === 11 && d.startsWith('1')) return d;
  if (/^[2-9]\d{9}$/.test(d)) return `1${d}`;
  if (d.startsWith('011') && d.length > 11) return d.slice(3);
  return null;
};

/** Reglas por país. Espejo de `WA_RULES` del admin (goautos-admin/src/utils/waDigits.ts). */
const PHONE_DIGITS: Record<string, PhoneDigitsRule> = {
  CL: chile,
  BR: brasil,
  CO: colombia,
  AR: argentina,
  US: estadosUnidos,
};

/**
 * Regla de dígitos del país (desconocido/null → CL). `propias` permite que un
 * módulo conserve su variante histórica para algún país (p.ej. la de Brasil de
 * `utils/functions`, que entiende el 0 de larga distancia nacional) sin volver
 * a un `if (país === …)`: se busca primero ahí, después en el récord, y si el
 * país no está en ninguno cae a CL (la propia, si la hay).
 */
export const phoneDigitsRule = (
  country?: string | null,
  propias?: Partial<Record<string, PhoneDigitsRule>>
): PhoneDigitsRule => {
  const code = codeOf(country);
  return propias?.[code] ?? PHONE_DIGITS[code] ?? propias?.[DEFAULT_COUNTRY] ?? PHONE_DIGITS[DEFAULT_COUNTRY];
};

/** Dígitos internacionales sin '+' de un teléfono, por país, o `null`. Ver `PhoneDigitsRule`. */
export const normalizePhoneDigits = (digits: string, country?: string | null): string | null =>
  phoneDigitsRule(country)(digits);

/** Regla "blanda" genérica: si no empieza con el código del país, se antepone. */
const anteponer = (dialCode: string) => (clean: string): string =>
  clean.startsWith(dialCode) ? clean : `${dialCode}${clean}`;

/**
 * Dígitos para `wa.me` de un número que el dueño del sitio escribió en el
 * builder (ya sin '+', espacios, guiones ni paréntesis): regla "blanda", nunca
 * null — si no se reconoce, se antepone el código del país y listo (el link
 * sale como siempre). CL y BR, tal cual lo hacían las secciones del builder;
 * CO/AR prueban la regla estricta y, si no calza, anteponen su código.
 */
const WA_LINK_DIGITS: Record<string, (clean: string) => string> = {
  CL: anteponer('56'),
  BR: (c) => (c.startsWith('55') && c.length >= 12 ? c : `55${c}`),
  CO: (c) => colombia(c) ?? anteponer('57')(c),
  AR: (c) => argentina(c) ?? anteponer('54')(c),
  US: (c) => estadosUnidos(c) ?? anteponer('1')(c),
};

export const waLinkDigits = (clean: string, country?: string | null): string =>
  (WA_LINK_DIGITS[codeOf(country)] ?? anteponer(dialCodeOf(country)))(clean);

/**
 * ¿El sitio puede ofrecer el modo "tasador" de Compra Directa (tasa con la sola
 * patente)? Espejo de `CountryFeatures.appraiserOnSite`: solo Chile tiene
 * tasador por patente (GetAPI); el resto cae al modo visitante (pide precio).
 */
const APPRAISER_ON_SITE: Record<string, boolean> = {
  CL: true,
  BR: false,
  CO: false,
  AR: false,
  US: false,
};

export const appraiserOnSite = (country?: string | null): boolean =>
  APPRAISER_ON_SITE[codeOf(country)] ?? APPRAISER_ON_SITE[DEFAULT_COUNTRY];

/**
 * Documento del CLIENTE FINAL que piden los formularios del sitio (hoy, el de
 * financiamiento). Espejo de `CountryFeatures.taxId` del admin
 * (goautos-admin/src/lib/country.ts): va por PAÍS y no por idioma, con sus
 * mismos textos (etiqueta y ejemplo de una persona).
 *
 * Contrato (el del admin): CL, BR, null y desconocido devuelven `null` = "lo de
 * hoy, sin tocar": Chile sigue con la etiqueta i18n "RUT" sin ejemplo, y
 * Brasil con la traducción pt "CPF". Solo CO y AR nombran su documento.
 *
 * Sin validación A PROPÓSITO, en ningún país: es un formulario de leads y un
 * lead no se pierde por cómo se escribió el documento (Chile tampoco lo valida).
 */
export interface CustomerDoc {
  /** Etiqueta del campo; el correo a la automotora la usa como rótulo (con ':'). */
  label: string;
  /** Ejemplo del input (documento de una persona, el mismo del admin). */
  placeholder: string;
  /**
   * El campo NO es obligatorio. EE.UU.: un formulario público no debe empujar
   * a escribir el SSN; el documento queda para cuando la automotora lo pida.
   */
  optional?: boolean;
}

const CUSTOMER_DOC: Record<string, CustomerDoc | null> = {
  CL: null,
  BR: null,
  // Colombia: cédula de ciudadanía (persona) o NIT (empresa, con DV).
  CO: { label: 'NIT/Cédula', placeholder: '1.234.567.890' },
  // Argentina: DNI (persona) o CUIT/CUIL.
  AR: { label: 'CUIT/DNI', placeholder: '12.345.678' },
  // EE.UU.: licencia de conducir (persona) o EIN (empresa), opcional; nunca el SSN.
  US: { label: 'ID/EIN', placeholder: 'D1234567', optional: true },
};

export const customerDocOf = (country?: string | null): CustomerDoc | null =>
  CUSTOMER_DOC[codeOf(country)] ?? null;

/**
 * ¿El país tiene IVA a la chilena (factura afecta, crédito fiscal)? Espejo de
 * `CountryFeatures.vatUi` del admin. Brasil y EE.UU. no (BR: ICMS por estado;
 * US: sales tax que paga el comprador). Desconocido/null → Chile.
 */
const VAT_UI: Record<string, boolean> = {
  CL: true,
  BR: false,
  CO: true,
  AR: true,
  US: false,
};

export const vatUiOf = (country?: string | null): boolean =>
  VAT_UI[codeOf(country)] ?? VAT_UI[DEFAULT_COUNTRY];
